import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getFirestore,
  query,
  collection,
  orderBy,
  limit,
  startAfter,
  getDocs,
  doc,
  getDoc,
  Timestamp,
  onSnapshot,
  deleteDoc,
  where,
  getCountFromServer,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { normalizeSong } from '../utils/songUtils';
import logger from '../utils/logger';
import {
  isTransientFirestoreError,
  withFirestoreRetry,
  withTimeout,
} from '../utils/firestoreRetry';

const db = getFirestore();

async function getCountFromServerWithRetry(countQuery, attempts = 3) {
  return withFirestoreRetry(() => getCountFromServer(countQuery), { attempts });
}

const logFirestoreCountError = (message, error) => {
  if (isTransientFirestoreError(error)) {
    logger.warn(message, error);
    return;
  }
  logger.error(message, error);
};

function isUsableRandomSong(song, currentSongId) {
  return (
    song &&
    song.id &&
    song.URL &&
    song.songName &&
    song.songArtist &&
    (currentSongId ? song.id !== currentSongId : true)
  );
}

const useSongFeed = () => {
  const [songs, setSongs] = useState([]);
  const [totalSongCount, setTotalSongCount] = useState(0);
  const [currentSong, setCurrentSong] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const songsRef = useRef([]);
  /** Next full-catalog random for `getNextSong(null)` (e.g. home Random button). */
  const prefetchedHomeRandomRef = useRef(null);
  const pageSize = 50;

  useEffect(() => {
    songsRef.current = songs;
  }, [songs]);

  useEffect(() => {
    const getTotalSongCount = async () => {
      try {
        const snapshot = await getCountFromServerWithRetry(
          collection(db, 'songs')
        );
        setTotalSongCount(snapshot.data().count);
      } catch (error) {
        logFirestoreCountError('Error getting total song count:', error);
      }
    };

    getTotalSongCount();

    logger.info('[SongsContext] Setting up song listener');
    const initialQuery = query(
      collection(db, 'songs'),
      orderBy('timestamp', 'desc'),
      limit(pageSize)
    );

    const unsubscribe = onSnapshot(
      initialQuery,
      (snapshot) => {
        logger.debug('[SongsContext] Received song update');
        const songList = snapshot.docs.map((d) =>
          normalizeSong({ id: d.id, ...d.data() })
        );
        setSongs(songList);
        songsRef.current = songList;
      },
      (error) => {
        logger.error('[SongsContext] Error in song listener:', error);
      }
    );

    return () => {
      logger.debug('[SongsContext] Cleaning up song listener');
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const fetchInitialSongs = async () => {
      try {
        const today = new Date();
        const twoDaysAgo = new Date();
        twoDaysAgo.setDate(today.getDate() - 2);

        const initialQuery = query(
          collection(db, 'songs'),
          where('timestamp', '<=', Timestamp.fromDate(today)),
          where('timestamp', '>=', Timestamp.fromDate(twoDaysAgo)),
          orderBy('timestamp', 'desc')
        );

        const snapshot = await getDocs(initialQuery);
        const songList = snapshot.docs.map((d) =>
          normalizeSong({ id: d.id, ...d.data() })
        );

        setSongs(songList);
        songsRef.current = songList;
      } catch (error) {
        logger.error('Error fetching initial songs:', error);
      }
    };

    fetchInitialSongs();
  }, []);

  const loadMoreSongs = useCallback(async () => {
    if (loadingMore) return;

    // Page from the oldest song already loaded, so the realtime listener and the
    // initial fetch can overlap without load-more re-fetching what's on screen.
    let oldest = null;
    for (const song of songsRef.current) {
      const ts = song.timestamp;
      if (typeof ts?.toMillis !== 'function') continue;
      if (!oldest || ts.toMillis() < oldest.toMillis()) oldest = ts;
    }
    if (!oldest) return;

    setLoadingMore(true);
    try {
      const nextQuery = query(
        collection(db, 'songs'),
        where('timestamp', '<', oldest),
        orderBy('timestamp', 'desc'),
        limit(pageSize)
      );

      const snapshot = await getDocs(nextQuery);
      const fetchedSongs = snapshot.docs.map((d) =>
        normalizeSong({ id: d.id, ...d.data() })
      );

      const merge = (prev) => {
        const seen = new Set(prev.map((song) => song.id));
        return [...prev, ...fetchedSongs.filter((song) => !seen.has(song.id))];
      };

      setSongs(merge);
      songsRef.current = merge(songsRef.current);
    } catch (error) {
      logger.error('Error loading more songs:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, pageSize]);

  const deleteSong = useCallback(async (songId) => {
    try {
      await deleteDoc(doc(db, 'songs', songId));
      setSongs((prev) => prev.filter((song) => song.id !== songId));
      logger.info(`Song with ID ${songId} deleted successfully.`);
    } catch (error) {
      logger.error('Error deleting song:', error);
    }
  }, []);

  const getFallbackSong = useCallback(
    async (currentSongId, depth = 0) => {
      if (depth > 2) {
        logger.debug('[SongsContext] Fallback: depth limit (loadMore did not yield songs)');
        return null;
      }

      const available = songsRef.current.filter(
        (song) =>
          song.id !== currentSongId &&
          song.URL &&
          song.songName &&
          song.songArtist
      );

      if (available.length > 0) {
        const fallback = available[Math.floor(Math.random() * available.length)];
        logger.debug('[SongsContext] Fallback song selected:', fallback.id);
        return fallback;
      }

      if (loadMoreSongs && !loadingMore) {
        logger.debug('[SongsContext] Attempting to load more songs for fallback');
        await loadMoreSongs();
        return getFallbackSong(currentSongId, depth + 1);
      }

      logger.debug('[SongsContext] No valid fallback songs available');
      return null;
    },
    [loadMoreSongs, loadingMore]
  );

  /**
   * Warms cache for `getNextSong(null)` — full DB random via same callable as on-demand.
   * Safe to call multiple times; last successful response wins.
   */
  const prefetchHomeRandomSong = useCallback(async () => {
    try {
      const getRandomSongFn = httpsCallable(functions, 'getRandomSong');
      const result = await getRandomSongFn({
        currentId: null,
        environment: process.env.NODE_ENV,
      });
      const randomSong = result.data;
      if (isUsableRandomSong(randomSong, null)) {
        prefetchedHomeRandomRef.current = randomSong;
        logger.debug('[SongsContext] Prefetched home random song:', randomSong.id);
      }
    } catch (error) {
      logger.debug('[SongsContext] Prefetch getRandomSong failed:', error);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const run = () => {
      prefetchHomeRandomSong();
    };
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(run, { timeout: 5000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = window.setTimeout(run, 2000);
    return () => window.clearTimeout(t);
  }, [prefetchHomeRandomSong]);

  const getNextSong = useCallback(
    async (currentSongId) => {
      logger.debug('[SongsContext] Fetching a random song');
      try {
        if (!currentSongId) {
          const cached = prefetchedHomeRandomRef.current;
          if (isUsableRandomSong(cached, null)) {
            prefetchedHomeRandomRef.current = null;
            prefetchHomeRandomSong();
            logger.debug('[SongsContext] Using prefetched random song:', cached.id);
            return cached;
          }
        }

        const getRandomSongFn = httpsCallable(functions, 'getRandomSong');
        logger.debug('[SongsContext] Calling getRandomSong with currentId:', currentSongId || null);

        const result = await getRandomSongFn({
          currentId: currentSongId || null,
          environment: process.env.NODE_ENV,
        });

        logger.debug('[SongsContext] Got response from backend');

        const randomSong = result.data;
        if (isUsableRandomSong(randomSong, currentSongId)) {
          logger.debug('[SongsContext] Random song selected:', randomSong.id);
          if (!currentSongId) {
            prefetchHomeRandomSong();
          }
          return randomSong;
        }

        logger.debug('[SongsContext] Invalid backend result, falling back locally');
        return await getFallbackSong(currentSongId);
      } catch (error) {
        logger.error('[SongsContext] Error in getNextSong, using fallback:', error);
        return await getFallbackSong(currentSongId);
      }
    },
    [getFallbackSong, prefetchHomeRandomSong]
  );

  const getSongById = useCallback(async (songId) => {
    const songDoc = await withTimeout(
      withFirestoreRetry(() => getDoc(doc(db, 'songs', songId))),
      8000
    );
    if (songDoc.exists()) {
      return normalizeSong({ id: songDoc.id, ...songDoc.data() });
    }
    logger.warn('No such song!');
    return null;
  }, []);

  const fetchSongsByUser = useCallback(async (userId) => {
    try {
      const snapshot = await getDocs(
        query(collection(db, 'songs'), where('userId', '==', userId))
      );
      const result = snapshot.docs.map((d) =>
        normalizeSong({ id: d.id, ...d.data() })
      );
      logger.debug('Fetched user songs:', result.length);
      return result;
    } catch (error) {
      logger.error('Error fetching user songs:', error);
      return [];
    }
  }, []);

  /**
   * Paginated fetch for user's shared songs.
   * Requires composite index: (userId, timestamp).
   */
  const fetchSongsByUserPaginated = useCallback(async (userId, opts = {}) => {
    const { limit: pageLimit = 20, order = 'desc', lastDoc = null } = opts;
    try {
      const constraints = [
        where('userId', '==', userId),
        orderBy('timestamp', order),
        limit(pageLimit + 1),
      ];
      if (lastDoc) constraints.push(startAfter(lastDoc));

      const snapshot = await getDocs(
        query(collection(db, 'songs'), ...constraints)
      );
      const docs = snapshot.docs;
      const hasMore = docs.length > pageLimit;
      const pageDocs = hasMore ? docs.slice(0, pageLimit) : docs;
      const nextLastDoc = pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null;
      const result = pageDocs.map((d) => normalizeSong({ id: d.id, ...d.data() }));

      return { songs: result, lastDoc: nextLastDoc, hasMore };
    } catch (error) {
      logger.error('Error fetching user songs (paginated):', error);
      return { songs: [], lastDoc: null, hasMore: false };
    }
  }, []);

  const fetchSharedSongsCount = useCallback(async (userId) => {
    try {
      const countSnapshot = await getCountFromServerWithRetry(
        query(collection(db, 'songs'), where('userId', '==', userId))
      );
      return countSnapshot.data().count;
    } catch (error) {
      logFirestoreCountError('Error fetching shared songs count:', error);
      return 0;
    }
  }, []);

  return {
    songs,
    setSongs,
    totalSongCount,
    currentSong,
    setCurrentSong,
    loadingMore,
    loadMoreSongs,
    deleteSong,
    getNextSong,
    getSongById,
    fetchSongsByUser,
    fetchSongsByUserPaginated,
    fetchSharedSongsCount,
  };
};

export default useSongFeed;
